/**
 * ChatWidget — floating guided wizard for demand-intelligence chatbot.
 *
 * Architecture:
 *   - Launcher button fixed bottom-right
 *   - On open: chat panel with header (brand + restart button + ✕),
 *     scrollable message thread, active SlotInput below bubbles, and footer CTA
 *   - Opening turn: Composer free-text → POST with optional freeText for AI pre-fill
 *   - Steps 1+: POST to /resources/chat and advance to next reply, accumulating
 *     bot + user bubbles in a transcript
 *   - sessionId: crypto.randomUUID() stored in localStorage 'cs_chat_session'
 *   - conversationId: kept in component state from first server response
 *   - CSRF: useAuthenticityToken() → appended as 'csrf' field to FormData
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuthenticityToken } from 'remix-utils/csrf/react';
import { GoogleMapsProvider } from '~/components/googleMapsProvider';
import { SlotInput, SLOT_HEADLINES, type SlotValue } from './wizardStep';
import type { ChatReply } from '~/.server/chat/orchestrator.server';
import type { SlotName, SlotState } from '~/.server/chat/slots';
import { type ChatMessage, MessageBubble, TypingIndicator } from './chatThread';
import { Composer } from './composer';
import { answerLabel } from './answerLabel';
import { useCategories } from '~/utils/hooks/useCategories';

// ── Constants ──────────────────────────────────────────────────────────────

const SESSION_KEY = 'cs_chat_session';

// ── Helpers ────────────────────────────────────────────────────────────────

function getOrCreateSessionId(): string {
  if (typeof window === 'undefined') return '';
  let id = localStorage.getItem(SESSION_KEY);
  if (!id) {
    id = crypto.randomUUID();
    localStorage.setItem(SESSION_KEY, id);
  }
  return id;
}

function synthesizeSummary(state: Partial<SlotState>): string {
  const bits: string[] = [];
  if (state.businessType) bits.push(state.businessType);
  if (state.neighborhood) bits.push(state.neighborhood);
  else if (state.city) bits.push(state.city);
  if (state.headcount) bits.push(`${state.headcount} personas`);
  if (state.budgetAmount) bits.push(`${state.budgetAmount}€/${state.budgetPeriod ?? 'mes'}`);
  return bits.join(' · ');
}

// ── Types ──────────────────────────────────────────────────────────────────

type WidgetPhase =
  | { kind: 'launcher' }
  | { kind: 'greeting' }
  | { kind: 'wizard'; reply: ChatReply }
  | { kind: 'loading' };

interface ChatWidgetProps {
  locale?: string;
}

// ── Main widget ───────────────────────────────────────────────────────────

export function ChatWidget({ locale = 'es' }: ChatWidgetProps) {
  const csrf = useAuthenticityToken();
  const { data: categories = [] } = useCategories();

  // Panel open/close
  const [isOpen, setIsOpen] = useState(false);

  // Phase of the wizard
  const [phase, setPhase] = useState<WidgetPhase>({ kind: 'greeting' });

  // Server state
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [slotState, setSlotState] = useState<Partial<SlotState>>({});

  // Current slot's pending value (what user has selected but not yet confirmed)
  const [pendingSlot, setPendingSlot] = useState<SlotName | null>(null);
  const [pendingValue, setPendingValue] = useState<SlotValue | null>(null);

  // Error state
  const [error, setError] = useState<string | null>(null);

  // ── Step 1: Transcript state ─────────────────────────────────────────────
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const msgIdRef = useRef(0);
  const nextId = () => `m${msgIdRef.current++}`;
  const threadEndRef = useRef<HTMLDivElement | null>(null);

  const sessionIdRef = useRef<string>('');
  // Ref kept in sync with slotState so postTurn always reads the current value
  // even when called from a stale closure (e.g. right after handleReset).
  const slotStateRef = useRef<Partial<SlotState>>({});

  useEffect(() => {
    sessionIdRef.current = getOrCreateSessionId();
  }, []);

  // Keep ref in sync whenever slotState changes.
  useEffect(() => {
    slotStateRef.current = slotState;
  }, [slotState]);

  // ── Step 2: Bot bubble helper ────────────────────────────────────────────

  const pushBotForReply = useCallback((reply: ChatReply) => {
    if (reply.kind === 'ask') {
      const meta = SLOT_HEADLINES[reply.slot] ?? { q: reply.slot };
      setMessages((m) => [...m, { id: nextId(), role: 'bot', slot: reply.slot, text: meta.q, hint: meta.hint }]);
    } else {
      const confirmation = reply.text;
      const text = reply.cards.length
        ? `Te recomiendo ${reply.cards.length} espacio${reply.cards.length !== 1 ? 's' : ''} 👇`
        : 'Aún no hay coincidencias exactas, pero seguiremos buscando y te avisaremos.';
      setMessages((m) => [...m, { id: nextId(), role: 'bot-cards', text, cards: reply.cards, confirmation }]);
    }
  }, []);

  // ── Post a turn to the server ──────────────────────────────────────────

  const postTurn = useCallback(
    async (opts: {
      slot?: SlotName;
      value?: SlotValue;
      freeText?: string;
      userEcho?: string;
      restoreReplyOnError?: ChatReply;
    }) => {
      setPhase({ kind: 'loading' });
      setError(null);

      // Optimistically append the user bubble so the transcript feels instant.
      // We remember its id so we can roll it back if the fetch fails.
      const echoId = opts.userEcho ? nextId() : null;
      if (echoId) {
        const echo = opts.userEcho as string;
        setMessages((m) => [...m, { id: echoId, role: 'user', text: echo }]);
      }

      const body = new FormData();
      body.append('csrf', csrf);
      body.append(
        'payload',
        JSON.stringify({
          // Read from ref, not from the closed-over state value, so that after
          // handleReset the first POST of a new journey carries {} rather than
          // the previous run's fully-populated SlotState.
          state: slotStateRef.current,
          sessionId: sessionIdRef.current,
          locale,
          slot: opts.slot,
          value: opts.value,
          freeText: opts.freeText,
        }),
      );

      try {
        const res = await fetch('/resources/chat', { method: 'POST', body });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = (await res.json()) as {
          conversationId?: string;
          reply: ChatReply;
          state: SlotState;
        };

        // conversationId only exists on the close turn (for reference); the
        // client doesn't need it to function.
        if (data.conversationId) setConversationId(data.conversationId);
        setSlotState(data.state);

        // Clear pending for new step
        setPendingSlot(null);
        setPendingValue(null);

        setPhase({ kind: 'wizard', reply: data.reply });

        // ── Step 4: Append next bot bubble ──────────────────────────────
        pushBotForReply(data.reply);
      } catch (e) {
        setError('Algo ha ido mal. Inténtalo de nuevo.');
        // Roll back the optimistic user bubble so no orphan answer remains.
        if (echoId) setMessages((m) => m.filter((msg) => msg.id !== echoId));
        // Restore the on-screen question so the user can retry (pending value is
        // preserved), instead of collapsing a mid-journey error to the greeting.
        if (opts.restoreReplyOnError) {
          setPhase({ kind: 'wizard', reply: opts.restoreReplyOnError });
        } else {
          setPhase((prev) => (prev.kind === 'loading' ? { kind: 'greeting' } : prev));
        }
      }
    },
    [csrf, locale, pushBotForReply],
  );

  // ── Handlers ───────────────────────────────────────────────────────────

  const OPENING_BUBBLE: ChatMessage = {
    id: 'm-open',
    role: 'bot',
    text: '¡Hola! 👋 Te ayudo a encontrar tu espacio. Cuéntame qué buscas o pulsa enviar para empezar.',
  };

  const handleOpen = () => {
    setIsOpen(true);
    // ── Step 5: Seed opening bubble when transcript is empty ──────────
    setMessages((m) =>
      m.length === 0 ? [OPENING_BUBBLE] : m,
    );
  };

  const handleClose = () => setIsOpen(false);

  const handleReset = () => {
    setIsOpen(true); // stay open on a fresh greeting (don't close the panel)
    setConversationId(null);
    slotStateRef.current = {};
    setSlotState({});
    setPendingSlot(null);
    setPendingValue(null);
    setError(null);
    setPhase({ kind: 'greeting' });
    // New session on reset
    localStorage.removeItem(SESSION_KEY);
    sessionIdRef.current = getOrCreateSessionId();
    // Reset id counter and transcript, then seed fresh opening bubble
    msgIdRef.current = 0;
    setMessages([OPENING_BUBBLE]);
  };

  /** Called when user picks a value in SlotInput */
  const handleSlotValue = useCallback((slot: SlotName, value: SlotValue) => {
    setPendingSlot(slot);
    setPendingValue(value);
  }, []);

  // ── Step 3: User bubble + Continuar ─────────────────────────────────────
  /** "Continuar" CTA — appends user bubble then posts the turn */
  const handleContinue = useCallback(() => {
    if (phase.kind !== 'wizard') return;
    const reply = (phase as { kind: 'wizard'; reply: ChatReply }).reply;
    if (reply.kind === 'ask' && pendingSlot && pendingValue != null) {
      postTurn({
        slot: pendingSlot,
        value: pendingValue ?? undefined,
        userEcho: answerLabel(pendingSlot, pendingValue, categories),
        restoreReplyOnError: reply,
      });
    }
  }, [phase, pendingSlot, pendingValue, postTurn, categories]);

  // ── Step 5: Composer drives the opening / greeting turn ──────────────────
  const handleComposerSend = useCallback((text: string) => {
    if (phase.kind === 'greeting' || messages.length === 0) {
      postTurn({ freeText: text || undefined, userEcho: text || undefined });
    }
  }, [phase, messages.length, postTurn]);

  // ── Derived ────────────────────────────────────────────────────────────

  const currentReply = phase.kind === 'wizard' ? (phase as { kind: 'wizard'; reply: ChatReply }).reply : null;

  // ── Step 7: Auto-scroll ─────────────────────────────────────────────────
  useEffect(() => {
    threadEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [messages, phase]);

  // ── Render ─────────────────────────────────────────────────────────────

  return (
    <GoogleMapsProvider>
      {/* Launcher */}
      {!isOpen && (
        <button
          type="button"
          aria-label="Encontrar espacio"
          onClick={handleOpen}
          className="fixed bottom-6 right-6 z-50 flex items-center gap-2 rounded-full bg-neutral-900 px-5 py-3.5 text-sm font-bold text-white shadow-lg transition-transform hover:scale-105 hover:shadow-xl focus:outline-none"
        >
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <path d="m21 21-4-4" />
            <circle cx="11" cy="11" r="7" />
          </svg>
          Encuentra tu espacio
        </button>
      )}

      {/* Panel */}
      {isOpen && (
        <div className="fixed bottom-6 right-6 z-50 flex w-[360px] max-w-[calc(100vw-2rem)] flex-col overflow-hidden rounded-2xl border border-neutral-200 bg-white shadow-2xl">
          {/* ── Step 6: Header — brand + restart + close ─────────────────── */}
          <div className="flex items-center justify-between border-b border-neutral-100 px-5 py-3.5">
            <div className="flex items-center gap-2.5">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-neutral-900 text-xs font-bold text-white">
                CE
              </div>
              <span className="text-sm font-semibold text-neutral-900">Encuentra tu espacio</span>
            </div>
            <div className="flex items-center gap-1">
              {/* Restart button */}
              <button
                type="button"
                aria-label="Empezar de nuevo"
                onClick={handleReset}
                className="flex h-7 w-7 items-center justify-center rounded-full text-neutral-400 transition-colors hover:bg-neutral-100 hover:text-neutral-900"
              >
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M3 12a9 9 0 1 0 3-6.7L3 8" /><path d="M3 3v5h5" />
                </svg>
              </button>
              {/* Close button */}
              <button
                type="button"
                aria-label="Cerrar"
                onClick={handleClose}
                className="flex h-7 w-7 items-center justify-center rounded-full text-neutral-400 transition-colors hover:bg-neutral-100 hover:text-neutral-900"
              >
                ✕
              </button>
            </div>
          </div>

          {/* ── Step 6: Body — thread + active SlotInput + typing indicator ── */}
          <div role="log" aria-live="polite" className="flex flex-1 flex-col gap-3 overflow-y-auto px-4 py-4" style={{ maxHeight: 380 }}>
            {error && <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-600">{error}</p>}
            {messages.map((m) => <MessageBubble key={m.id} message={m} />)}
            {phase.kind === 'wizard' && currentReply?.kind === 'ask' && (
              <div className="ml-8 mt-1">
                <SlotInput
                  reply={(phase as { kind: 'wizard'; reply: ChatReply }).reply}
                  pending={pendingValue}
                  onValue={handleSlotValue}
                  onRequestEmail={handleContinue}
                  stateSummary={synthesizeSummary(slotState)}
                  contactEmail={slotState.contact?.email ?? undefined}
                  citySlug={slotState.city}
                  cityLat={slotState.lat}
                  cityLng={slotState.lng}
                />
              </div>
            )}
            {phase.kind === 'loading' && <TypingIndicator />}
            <div ref={threadEndRef} />
          </div>

          {/* ── Step 6: Footer ──────────────────────────────────────────────── */}
          <div className="border-t border-neutral-100 px-4 py-3">
            {currentReply?.kind === 'close' ? (
              /* Close screen → restart */
              <button
                type="button"
                onClick={handleReset}
                className="w-full rounded-xl border border-neutral-200 py-2.5 text-xs text-neutral-400 hover:bg-neutral-50"
              >
                Empezar otra búsqueda
              </button>
            ) : currentReply?.kind === 'ask' ? (
              /* Structured ask step → Continuar (disabled until selection) */
              <button
                type="button"
                onClick={handleContinue}
                disabled={pendingValue === null}
                className={`w-full rounded-xl py-3 text-sm font-bold transition-colors ${
                  pendingValue === null
                    ? 'cursor-not-allowed bg-neutral-200 text-neutral-400'
                    : 'bg-neutral-900 text-white hover:bg-neutral-700'
                }`}
              >
                Continuar
              </button>
            ) : (
              /* Opening / greeting turn → free-text composer */
              <Composer
                placeholder={messages.length <= 1 ? 'Cuéntanos qué buscas…' : 'Escribe un mensaje…'}
                onSend={handleComposerSend}
                allowEmpty={phase.kind === 'greeting' || messages.length <= 1}
                disabled={phase.kind === 'loading'}
              />
            )}
          </div>
        </div>
      )}
    </GoogleMapsProvider>
  );
}
